import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import {
  Button,
  Card,
  CardHeader,
  Chip,
  Form,
  Input,
  Label,
  Separator,
  Switch
} from 'src/design-system';
import ReferralsApi from 'src/api/referrals';
import UsersApi from 'src/api/users';
import { ReferralDashboard } from 'src/api/types/referral';
import { UserSettings } from 'src/api/types/user';
import Loader from 'src/components/Loader/Loader';
import { useAppSelector } from 'src/store/hooks';
import { errorToast, successToast } from 'src/utils/toasts';

const CUSTOM_REFERRAL_CODE_MIN_LENGTH = 4;
const CUSTOM_REFERRAL_CODE_MAX_LENGTH = 32;
const CUSTOM_REFERRAL_CODE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{2,30}[a-z0-9])$/;
const CUSTOM_REFERRAL_CODE_HELP_TEXT =
  'Use 4-32 lowercase letters, numbers, or hyphens. Start and end with a letter or number.';

const formatCustomReferralCodeInput = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-');

const formatReferralCodeDisplay = (value?: string): string => value?.toLowerCase() ?? '';

const getCustomReferralCodeValidationError = (value: string): string | null => {
  const code = value.trim();
  if (!code) return null;
  if (code.length < CUSTOM_REFERRAL_CODE_MIN_LENGTH) {
    return `Referral code must be at least ${CUSTOM_REFERRAL_CODE_MIN_LENGTH} characters.`;
  }
  if (code.length > CUSTOM_REFERRAL_CODE_MAX_LENGTH) {
    return `Referral code must be ${CUSTOM_REFERRAL_CODE_MAX_LENGTH} characters or fewer.`;
  }
  if (!/^[a-z0-9-]+$/.test(code)) {
    return 'Use only lowercase letters, numbers, and hyphens.';
  }
  if (!CUSTOM_REFERRAL_CODE_PATTERN.test(code)) {
    return 'Referral code must start and end with a letter or number.';
  }
  return null;
};

const getReferralCodeRequestError = (reason?: string) => {
  switch (reason) {
    case 'invalid_code':
      return CUSTOM_REFERRAL_CODE_HELP_TEXT;
    case 'reserved_code':
      return 'That code is reserved. Please try a more personal code.';
    case 'code_unavailable':
      return 'That code is already taken or pending review.';
    default:
      return 'Failed to submit custom referral code request.';
  }
};

interface ReferralPanelProps {
  variant?: 'card' | 'plain';
}

const ReferralPanelContent = () => {
  const [userSettings, setUserSettings] = useState<UserSettings>({});
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const [referralDashboard, setReferralDashboard] = useState<ReferralDashboard | null>(null);
  const [isReferralLoading, setIsReferralLoading] = useState(true);
  const [isCopyingReferralLink, setIsCopyingReferralLink] = useState(false);
  const [customReferralCode, setCustomReferralCode] = useState('');
  const [isSubmittingReferralCodeRequest, setIsSubmittingReferralCodeRequest] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const customReferralCodeError = getCustomReferralCodeValidationError(customReferralCode);
  const canSubmitCustomReferralCode =
    Boolean(customReferralCode.trim()) && !customReferralCodeError;

  const fetchUserSettings = useCallback(async () => {
    try {
      setIsLoadingSettings(true);
      const response = await UsersApi.fetchUserSettings();
      setUserSettings(response.data);
    } catch (err) {
      console.error('Error fetching user settings:', err);
      errorToast({
        title: 'Error',
        description: 'Failed to load settings. Please refresh the page.'
      });
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  const fetchReferralDashboard = useCallback(async () => {
    try {
      setIsReferralLoading(true);
      const response = await ReferralsApi.fetchReferralDashboard();
      setReferralDashboard(response.data);
      setCustomReferralCode(
        formatReferralCodeDisplay(response.data.customCodeRequest?.requestedCode)
      );
    } catch (err) {
      console.error('Error fetching referral dashboard:', err);
      errorToast({
        title: 'Error',
        description: 'Failed to load referral details. Please refresh the page.'
      });
    } finally {
      setIsReferralLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchReferralDashboard();
      fetchUserSettings();
    }
  }, [user, fetchReferralDashboard, fetchUserSettings]);

  const updateSetting = async <T,>(key: keyof UserSettings, newValue: T) => {
    if (updateInProgress) {
      return;
    }
    setUpdateInProgress(true);
    setIsSettingsLoading(true);
    const previousValue = userSettings[key];
    setUserSettings((prev) => ({
      ...prev,
      [key]: newValue
    }));
    try {
      await UsersApi.updateUserSettings({
        [key]: newValue
      } as Partial<UserSettings>);
      successToast({
        title: 'Preferences updated',
        description: 'Your preferences have been saved.'
      });
    } catch (err) {
      console.error('Error updating user settings:', err);
      setUserSettings((prev) => ({
        ...prev,
        [key]: previousValue
      }));
      errorToast({
        title: 'Error',
        description: 'Failed to update preferences. Please try again.'
      });
    } finally {
      setIsSettingsLoading(false);
      setUpdateInProgress(false);
    }
  };

  const handleCopyReferralLink = useCallback(async () => {
    if (!referralDashboard?.referralLink) return;
    try {
      setIsCopyingReferralLink(true);
      await navigator.clipboard.writeText(referralDashboard.referralLink);
      successToast({
        title: 'Referral link copied',
        description: 'Share it with a friend to earn a free month after they qualify.'
      });
    } catch (err) {
      console.error('Failed to copy referral link:', err);
      errorToast({
        title: 'Copy failed',
        description: 'Please copy your referral link manually.'
      });
    } finally {
      setIsCopyingReferralLink(false);
    }
  }, [referralDashboard?.referralLink]);

  const handleCustomReferralCodeRequest = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const requestedCode = customReferralCode.trim();
      if (!requestedCode) return;
      if (customReferralCodeError) {
        errorToast({
          title: 'Request failed',
          description: customReferralCodeError
        });
        return;
      }

      try {
        setIsSubmittingReferralCodeRequest(true);
        const response = await ReferralsApi.requestCustomReferralCode(requestedCode);
        if (response.data.status === 'rejected') {
          errorToast({
            title: 'Request failed',
            description: getReferralCodeRequestError(response.data.reason)
          });
          return;
        }
        if (response.data.request) {
          setReferralDashboard((current) =>
            current
              ? {
                  ...current,
                  customCodeRequest: response.data.request
                }
              : current
          );
        }
        const activeReferralLink = response.data.referralLink;
        if (activeReferralLink) {
          setReferralDashboard((current) =>
            current
              ? {
                  ...current,
                  referralLink: activeReferralLink
                }
              : current
          );
        }
        successToast({
          title: response.data.request ? 'Referral code request sent' : 'Referral code active',
          description: response.data.request
            ? 'We will review it and update your referral link after approval.'
            : 'That referral code is already active on your account.'
        });
      } catch (err) {
        const responseReason = (err as { response?: { data?: { reason?: string } } }).response?.data
          ?.reason;
        errorToast({
          title: 'Request failed',
          description: getReferralCodeRequestError(responseReason)
        });
      } finally {
        setIsSubmittingReferralCodeRequest(false);
      }
    },
    [customReferralCode, customReferralCodeError]
  );

  if (isReferralLoading || isLoadingSettings) {
    return <Loader fullscreen={false} />;
  }

  if (!referralDashboard) {
    return <p className="text-sm">No referral data available.</p>;
  }

  return (
    <>
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium">Referral Code:</span>
        <span className="text-sm font-mono">
          {formatReferralCodeDisplay(referralDashboard.referralCode)}
        </span>
      </div>
      <div className="flex items-end gap-2">
        <Input
          aria-label="Referral Link"
          value={referralDashboard.referralLink}
          readOnly
          variant="secondary"
          className="flex-1"
        />
        <Button
          type="button"
          isIconOnly
          size="md"
          variant="secondary"
          onPress={handleCopyReferralLink}
          isPending={isCopyingReferralLink}
          aria-label="Copy referral link">
          <FontAwesomeIcon icon={faCopy} />
        </Button>
      </div>

      <Form onSubmit={handleCustomReferralCodeRequest} className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Label>Custom Referral Code</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              aria-label="Custom Referral Code"
              value={customReferralCode}
              onChange={(event) =>
                setCustomReferralCode(formatCustomReferralCodeInput(event.target.value))
              }
              placeholder="the-dms-ark"
              variant="secondary"
            />
            <Button
              type="submit"
              variant="secondary"
              isPending={isSubmittingReferralCodeRequest}
              isDisabled={!canSubmitCustomReferralCode}>
              Request
            </Button>
          </div>
          <p className={`text-xs ${customReferralCodeError ? 'text-danger' : 'text-default-500'}`}>
            {customReferralCodeError ?? CUSTOM_REFERRAL_CODE_HELP_TEXT}
          </p>
        </div>
        {referralDashboard.customCodeRequest && (
          <Chip
            variant="soft"
            color={
              referralDashboard.customCodeRequest.status === 'approved'
                ? 'success'
                : referralDashboard.customCodeRequest.status === 'denied'
                  ? 'danger'
                  : 'warning'
            }>
            {referralDashboard.customCodeRequest.status === 'pending'
              ? `Pending review: ${formatReferralCodeDisplay(referralDashboard.customCodeRequest.requestedCode)}`
              : referralDashboard.customCodeRequest.status === 'approved'
                ? `Approved: ${formatReferralCodeDisplay(referralDashboard.customCodeRequest.requestedCode)}`
                : `Denied: ${formatReferralCodeDisplay(referralDashboard.customCodeRequest.requestedCode)}`}
          </Chip>
        )}
      </Form>

      <Switch
        isSelected={userSettings.showReferralLinkOnSharedPages ?? false}
        onChange={(isSelected) => updateSetting('showReferralLinkOnSharedPages', isSelected)}
        isDisabled={isSettingsLoading}
        className="w-full">
        <Switch.Content className="flex w-full items-center justify-between gap-3 rounded-md bg-default-100 p-3 text-left">
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              Show my referral link on shared campaigns and sessions
            </span>
            <span className="mt-1 text-xs">
              Adds a button to public share pages that links through your referral code.
            </span>
          </div>
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
        </Switch.Content>
      </Switch>

      <div className="grid grid-cols-2 gap-2">
        <Chip variant="soft">Pending: {referralDashboard.summary.pending}</Chip>
        <Chip variant="soft">Qualified: {referralDashboard.summary.qualified}</Chip>
        <Chip variant="soft">Granted: {referralDashboard.summary.granted}</Chip>
        <Chip variant="soft">Applied: {referralDashboard.summary.applied}</Chip>
      </div>

      <p className="text-xs">
        Reward criteria: friend must complete their first session and complete a first successful
        paid subscription charge.
      </p>
    </>
  );
};

const ReferralPanel: React.FC<ReferralPanelProps> = ({ variant = 'card' }) => {
  if (variant === 'plain') {
    return (
      <div className="flex flex-col gap-4">
        <ReferralPanelContent />
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="text-center flex flex-col items-center">
        <h1 className="text-2xl font-bold">Refer & Earn</h1>
        <p>Share your signup link. Earn 1 month free for each qualified friend.</p>
      </CardHeader>
      <Separator />
      <Card.Content className="flex flex-col gap-4">
        <ReferralPanelContent />
      </Card.Content>
    </Card>
  );
};

export default ReferralPanel;
